import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { User, McpStatus, McpTool } from '../types';
import { apiService } from '../services/api';

/**
 * Result of a single `loginWithW3` call.
 *  • `success`   — tokens were returned and local session is established
 *  • `challenge` — Cognito needs another round-trip (MFA / new-password); caller
 *                  must collect the right input from the user and re-call with
 *                  the returned `session`.
 */
export type W3Challenge =
  | 'SOFTWARE_TOKEN_MFA'
  | 'EMAIL_OTP'
  | 'SMS_MFA'
  | 'SELECT_MFA_TYPE'
  | 'MFA_SETUP'
  | 'NEW_PASSWORD_REQUIRED';

export type LoginResult =
  | { kind: 'success' }
  | {
      kind: 'challenge';
      challenge: W3Challenge;
      session: string;
      message: string;
      /** Masked code destination for EMAIL_OTP / SMS_MFA (e.g. "m***@h***.co.il"). */
      destination?: string;
      /** Methods to choose from on the SELECT_MFA_TYPE challenge. */
      availableMfaTypes?: string[];
    };

interface LoginParams {
  email: string;
  password?: string;
  session?: string;
  mfaCode?: string;
  /** Which MFA method the code belongs to (echoes the challenge name). */
  mfaType?: string;
  /** SELECT_MFA_TYPE: the method the user picked to continue with. */
  mfaSelection?: string;
  newPassword?: string;
}

interface AuthContextType {
  user: User | null;
  mcpStatus: McpStatus | null;
  mcpTools: McpTool[];
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithW3: (params: LoginParams) => Promise<LoginResult>;
  refreshMcpStatus: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [mcpStatus, setMcpStatus] = useState<McpStatus | null>(null);
  const [mcpTools, setMcpTools] = useState<McpTool[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadMcpStatusInBackground = useCallback(() => {
    apiService.getMcpStatus()
      .then((status) => {
        setMcpStatus(status);
        setMcpTools(status.tools || []);
      })
      .catch(() => {
        setMcpStatus(null);
        setMcpTools([]);
      });
  }, []);

  const loadUser = useCallback(async () => {
    const storedToken = localStorage.getItem('access_token');
    if (!storedToken) {
      setIsLoading(false);
      return;
    }
    try {
      const currentUser = await apiService.getCurrentUser();
      setUser(currentUser);
      loadMcpStatusInBackground();
    } catch (err: any) {
      // Only clear token on 401 (invalid/expired token).
      // Network errors, 500s, etc. should NOT log the user out.
      if (err.response?.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('w3_org_id');
      }
    } finally {
      setIsLoading(false);
    }
  }, [loadMcpStatusInBackground]);

  const refreshMcpStatus = useCallback(async () => {
    const status = await apiService.getMcpStatus();
    setMcpStatus(status);
    setMcpTools(status.tools || []);
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const loginWithW3 = useCallback(async (params: LoginParams): Promise<LoginResult> => {
    const result = await apiService.w3Login(params);

    // Branch on challenge first — Cognito sends 200 with no tokens when more steps are needed.
    if (result.challenge && result.session) {
      return {
        kind: 'challenge',
        challenge: result.challenge,
        session: result.session,
        message: result.message || '',
        destination: result.destination,
        availableMfaTypes: result.available_mfa_types,
      };
    }

    if (!result.access_token) {
      throw new Error('Unexpected login response: no token and no challenge');
    }

    localStorage.setItem('access_token', result.access_token);
    if (result.org_id) {
      localStorage.setItem('w3_org_id', result.org_id);
    }
    const currentUser = await apiService.getCurrentUser();
    setUser(currentUser);
    loadMcpStatusInBackground();
    return { kind: 'success' };
  }, [loadMcpStatusInBackground]);

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('w3_org_id');
    setUser(null);
    setMcpStatus(null);
    setMcpTools([]);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        mcpStatus,
        mcpTools,
        isAuthenticated: !!user,
        isLoading,
        loginWithW3,
        refreshMcpStatus,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
